import React from 'react';
import { useTranslation } from 'react-i18next';

import { Box, Divider, Fab, IconButton, Paper, Tooltip, Typography } from '@mui/material';
import { Add, CheckCircle, InfoOutlined, Remove } from '@mui/icons-material';

import ImagesSlideView from 'Components/ImagesSlideView';

const FALLBACK_IMAGE = '/images/brp-logo.png';

const CartRedirectionLinkedItemCard = ({
  item,
  index,
  isSelected,
  selectedQuantity,
  isItemAdded,
  onToggleSelect,
  onIncreaseQuantity,
  onDecreaseQuantity,
  getImageUrl,
  currencyName,
}) => {
  const { t, i18n } = useTranslation();
  const itemId = item?.id;
  const dealerPortalImages = (item?.files ?? []).filter(file => file?.forDealerPortal);
  const isItemSelected = Boolean(isSelected);
  const itemPrice = Number(item?.price || 0);
  const displayQuantity = Number(selectedQuantity || 0);

  const optionLabel = item?.selectedOptionLabel || item?.selectedOption || item?.optionLabel;
  const optionValue = item?.selectedOptionValue || item?.selectedOptionDescription || item?.optionValue;
  const currentLanguage = i18n.language;
  const isFrench = currentLanguage === 'fr';
  const podAdditionalInfoEn = item?.PODAdditionalInfoEn;
  const podAdditionalInfoFr = item?.PODAdditionalInfoFr;
  const podAdditionalInfo = (isFrench
    ? podAdditionalInfoFr || podAdditionalInfoEn
    : podAdditionalInfoEn || podAdditionalInfoFr || ''
  )?.trim();
  const podAdditionalInfoLabel = t('podAdditionalInfo');
  const linkedRows = Array.isArray(item?.bundledItems)
    ? item.bundledItems.map(linked => `${linked?.quantity || 1}X ${linked?.name || linked?.sku || ''}`.trim())
    : [];

  const squareActionSx = {
    borderRadius: 0,
    width: 36,
    height: 36,
    minHeight: 36,
    boxShadow: 2,
  };

  const handleSelect = event => {
    event.stopPropagation();
    if (itemId) {
      onIncreaseQuantity(itemId);
    }
  };

  const handleDeselect = event => {
    event.stopPropagation();
    if (itemId && displayQuantity > 0) {
      onDecreaseQuantity(itemId);
    }
  };

  return (
    <Paper
      elevation={3}
      onClick={itemId ? () => onToggleSelect(itemId) : undefined}
      sx={{
        width: '100%',
        borderRadius: 2.5,
        overflow: 'hidden',
        cursor: itemId ? 'pointer' : 'default',
        border: theme => `2px solid ${isItemSelected ? theme.palette.secondary.main : theme.palette.divider}`,
        backgroundColor: '#fff',
        transition: 'box-shadow 150ms ease, border-color 150ms ease, background-color 150ms ease',
      }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', p: 0 }}>
        <Box sx={{ p: 1.5, position: 'relative', backgroundColor: '#F7F7F7' }}>
          <Box
            sx={{
              '& img': {
                maxHeight: 170,
                width: '100%',
                objectFit: 'contain',
              },
            }}>
            <ImagesSlideView
              key={item?.id ?? `linked-item-${index}`}
              images={dealerPortalImages}
              getImageUrl={imageId => {
                if (item?.id && imageId) {
                  return getImageUrl(item.id, imageId);
                }
                return FALLBACK_IMAGE;
              }}
              defaultImage={FALLBACK_IMAGE}
              isLoading={false}
            />
          </Box>
          {isItemSelected && (
            <Box
              sx={{
                position: 'absolute',
                top: 10,
                right: 10,
                width: 28,
                height: 28,
                borderRadius: '50%',
                backgroundColor: '#fff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: 2,
              }}>
              <CheckCircle color="secondary" fontSize="small" />
            </Box>
          )}
        </Box>

        <Box sx={{ px: 2, pt: 1.5, pb: 1.25 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
            {item?.dealerPortalTitleFr || t('linkedItemNameFallback')}
          </Typography>
          <Typography sx={{ fontSize: 18, color: 'text.secondary', mt: 0.25 }}>
            {item?.sku || t('skuUnavailable')}
          </Typography>

          {(optionLabel || optionValue) && (
            <Box sx={{ mt: 1 }}>
              {optionLabel && (
                <Typography variant="body2" color="text.secondary">
                  {optionLabel}
                </Typography>
              )}
              {optionValue && (
                <Typography variant="body2" color="text.secondary">
                  {optionValue}
                </Typography>
              )}
            </Box>
          )}

          {linkedRows.length > 0 && (
            <>
              <Divider sx={{ my: 1.25 }} />
              {linkedRows.map((row, rowIndex) => (
                <Typography key={`${itemId || index}-${rowIndex}`} variant="body2" color="text.secondary" sx={{ mb: 0.25 }}>
                  {row}
                </Typography>
              ))}
            </>
          )}

          {podAdditionalInfo && (
            <>
              <Divider sx={{ my: 1.25 }} />
              <Box sx={{ mt: 0.25, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Typography variant="body2" sx={{ fontWeight: 700, color: 'text.secondary', fontSize: 14 }}>
                  {podAdditionalInfoLabel}
                </Typography>
                <Tooltip
                  title={
                    <Typography
                      component="span"
                      sx={{
                        display: 'block',
                        fontSize: 14,
                        lineHeight: 1.4,
                        whiteSpace: 'pre-wrap',
                      }}>
                      {podAdditionalInfo}
                    </Typography>
                  }
                  placement="top"
                  arrow
                  componentsProps={{
                    tooltip: {
                      sx: {
                        maxWidth: 'none',
                        px: 2,
                        py: 1.25,
                      },
                    },
                  }}>
                  <IconButton
                    size="small"
                    aria-label={podAdditionalInfoLabel}
                    onClick={event => event.stopPropagation()}
                    onMouseDown={event => event.stopPropagation()}
                    sx={{ p: 0.25, color: 'text.secondary' }}>
                    <InfoOutlined sx={{ fontSize: 18 }} />
                  </IconButton>
                </Tooltip>
              </Box>
            </>
          )}

          {!optionLabel && !optionValue && linkedRows.length === 0 && !podAdditionalInfo && (
            <Typography
              variant="caption"
              color={isItemAdded ? 'success.main' : 'text.secondary'}
              sx={{ display: 'block', mt: 0.75 }}>
              {isItemAdded ? t('addedToCart') : isItemSelected ? t('selectedForCart') : t('tapToSelect')}
            </Typography>
          )}
        </Box>

        <Divider />

        <Box sx={{ px: 2, py: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography fontWeight={700} sx={{ fontSize: 15 }}>
            {t('unitPrice')}
          </Typography>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            ${itemPrice.toFixed(2)} {currencyName || ''}
          </Typography>
        </Box>

        <Divider />

        <Box sx={{ px: 2, py: 1.25, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography fontWeight={700} sx={{ fontSize: 15 }}>
            {t('quantity')}
          </Typography>
          <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ width: 130 }}>
            <Fab size="small" color="secondary" sx={squareActionSx} disabled={displayQuantity <= 0} onClick={handleDeselect}>
              <Remove />
            </Fab>
            <Typography
              sx={{
                minWidth: 24,
                textAlign: 'center',
                fontWeight: isItemSelected ? 700 : 500,
                color: isItemSelected ? 'secondary.main' : 'text.primary',
              }}>
              {displayQuantity}
            </Typography>
            <Fab size="small" color="secondary" sx={{ ...squareActionSx, color: '#000' }} onClick={handleSelect}>
              <Add />
            </Fab>
          </Box>
        </Box>

        <Divider />

        <Box sx={{ px: 2, py: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
          <Typography fontWeight={700} sx={{ fontSize: 15 }}>
            {t('price')}
          </Typography>
          <Typography fontWeight={800} sx={{ fontSize: 24, lineHeight: 1.1 }}>
            ${(itemPrice * displayQuantity).toFixed(2)}
          </Typography>
        </Box>
      </Box>
    </Paper>
  );
};

export default CartRedirectionLinkedItemCard;
